/* eslint-disable jsx-a11y/media-has-caption */
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {globalWindow} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useConstant} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {
  CellsContext,
  CellsContextTypesEnum,
} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {isExpandedDarkTheme} from '../../../../../libs/shared/ui/src/theme/themeTypeHelpers';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {shouldShowInboxZero} from '../../../../../srcCommon/inboxes/viewer/empty/emptyViewerContentHelpers';
import inboxZero from '../../../../assets/images/inboxZero.svg';
import inboxZeroCustomViews from '../../../../assets/images/inboxZeroCustomViews.png';
import inboxZeroPoster1 from '../../../../assets/images/inboxZeroPoster1.png';
import inboxZeroPoster2 from '../../../../assets/images/inboxZeroPoster2.png';
import inboxZeroAnimatedMov1 from '../../../../assets/videos/inboxZeroAnimated1.mov';
import inboxZeroAnimated1 from '../../../../assets/videos/inboxZeroAnimated1.mp4';
import inboxZeroAnimatedWebm1 from '../../../../assets/videos/inboxZeroAnimated1.webm';
import inboxZeroAnimatedMov2 from '../../../../assets/videos/inboxZeroAnimated2.mov';
import inboxZeroAnimated2 from '../../../../assets/videos/inboxZeroAnimated2.mp4';
import inboxZeroAnimatedWebm2 from '../../../../assets/videos/inboxZeroAnimated2.webm';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {findThemedImageInfo, ThemedImageNamesEnum} from '../../../../helpers/imageThemeHelpers';
import {emojiMap} from '../../../../services/emojis/emojis';
import {getIsBackfillingCustomView} from '../../../../store/data/jobs/jobsSelectors';
import {InboxesParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {computeIsAllExceptSelection} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {InlineEmoji} from '../../../common/emoji/inlineEmoji';

/*
 * Props.
 */

interface EmptyViewerContentProps {
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  didBecomeEmpty: boolean;
}

/*
 * Style.
 */

function addViewerHeaderDivStyles() {
  return css`
    color: ${(p) => p.theme.greys.shade90};
    font-size: ${fontSizes.large};
    font-weight: ${fontWeights.semibold};
    line-height: 20px;
  `;
}

const StyledViewerDiv = styled.div`
  grid-row: 1 / span 4;
  grid-column: 1;
  ${center()};
`;

const StyledInboxZeroCustomViewImg = styled.img`
  width: 380px;
  height: 240px;
`;

interface ImageDimensions {
  height: number;
  width: number;
}

const StyledInboxZeroImg = styled.img<ImageDimensions>`
  width: ${(p) => p.width}px;
  height: ${(p) => p.height}px;
`;

const StyledCustomViewerHeaderDiv = styled.div`
  padding: 25px 0 13px;
  ${addViewerHeaderDivStyles()}
`;

const StyledViewerHeaderDiv = styled.div`
  padding: 25px 0 5px;
  ${addViewerHeaderDivStyles()}
`;

const StyledViewerBodyDiv = styled.div`
  text-align: center;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  line-height: 150%;
`;

const StyledInboxZeroVideo = styled.video`
  // Disable pointer events here so that users won't be able to access the video controls
  pointer-events: none;
`;

/*
 * Component.
 */

export const EmptyViewerContent: FC<EmptyViewerContentProps> = (props) => (
  <StyledViewerDiv>
    <InboxZeroMessage {...props} />
  </StyledViewerDiv>
);

const InboxZeroMessage: FC<EmptyViewerContentProps> = (props) => {
  const {cellsContext, inboxesParameters} = props;
  const theme = useUserTheme();
  const localize = useLocalize();
  const inboxZeroId = useConstant(() => Math.floor(Math.random() * 2));
  const isCustomViewLoading = useAppSelector(getIsBackfillingCustomView);
  const prefersReducedMotion = globalWindow.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // There aren't animated images for dark mode.
  const shouldShowAnimation = !prefersReducedMotion && props.didBecomeEmpty && !isExpandedDarkTheme(theme);

  // Avoid showing the "inbox zero" messaging for the edge case where we have an "all except" selection that resolves
  // to no conversations in P2 being selected.
  if (!shouldShowInboxZero(cellsContext) || computeIsAllExceptSelection(inboxesParameters.selection)) {
    return (
      <>
        <img
          draggable={false}
          src={inboxZero}
          alt={localize(
            defineMessage({
              id: 'viewer-inboxzero',
              message: 'Empty inbox',
            }),
          )}
        />
        <StyledViewerBodyDiv>
          <Trans id="empty-viewer-inboxzero">No conversations</Trans>
        </StyledViewerBodyDiv>
      </>
    );
  }

  if (cellsContext.type === CellsContextTypesEnum.CUSTOM_VIEW && isCustomViewLoading) {
    return (
      <>
        <StyledInboxZeroCustomViewImg
          draggable={false}
          src={inboxZeroCustomViews}
          alt={localize(
            defineMessage({
              id: 'viewer-inboxzero-customviews',
              message: 'Empty view',
            }),
          )}
        />
        <StyledCustomViewerHeaderDiv>
          <Trans id="empty-viewer-header-custom-view">Your views are loading</Trans>
        </StyledCustomViewerHeaderDiv>
        <StyledViewerBodyDiv>
          <Trans id="empty-viewer-body-custom-view">
            Check back again later <InlineEmoji emoji={emojiMap.hugging_face} size={14} />
          </Trans>
        </StyledViewerBodyDiv>
      </>
    );
  }

  const inboxZero1 = findThemedImageInfo(theme, ThemedImageNamesEnum.INBOX_ZERO_1);
  const inboxZero2 = findThemedImageInfo(theme, ThemedImageNamesEnum.INBOX_ZERO_2);
  switch (inboxZeroId) {
    case 1:
      return (
        <>
          {shouldShowAnimation ? (
            <StyledInboxZeroVideo
              autoPlay
              playsInline
              muted
              poster={inboxZeroPoster1}
              height={395}
              width={480}
            >
              <source src={inboxZeroAnimatedMov1} type='video/mp4; codecs="hvc1"' />
              <source src={inboxZeroAnimatedWebm1} type="video/webm" />
              <source src={inboxZeroAnimated1} type="video/mp4" />
            </StyledInboxZeroVideo>
          ) : (
            <StyledInboxZeroImg
              draggable={false}
              src={inboxZero1.src}
              width={inboxZero1.width}
              height={inboxZero1.height}
              alt={localize(
                defineMessage({
                  id: 'viewer-inboxzero1',
                  message: 'Empty inbox',
                }),
              )}
            />
          )}
          <StyledViewerHeaderDiv>
            <Trans id="empty-viewer-header1">Mission accomplished!</Trans>
          </StyledViewerHeaderDiv>
          <StyledViewerBodyDiv>
            <Trans id="empty-viewer-body1">
              Sit back and relax&hellip;
              <br />
              until the next message <InlineEmoji emoji={emojiMap.wink} size={14} />
            </Trans>
          </StyledViewerBodyDiv>
        </>
      );

    default:
      return (
        <>
          {shouldShowAnimation ? (
            <StyledInboxZeroVideo
              autoPlay
              playsInline
              muted
              poster={inboxZeroPoster2}
              height={395}
              width={480}
            >
              <source src={inboxZeroAnimatedMov2} type='video/mp4; codecs="hvc1"' />
              <source src={inboxZeroAnimatedWebm2} type="video/webm" />
              <source src={inboxZeroAnimated2} type="video/mp4" />
            </StyledInboxZeroVideo>
          ) : (
            <StyledInboxZeroImg
              draggable={false}
              src={inboxZero2.src}
              height={inboxZero2.height}
              width={inboxZero2.width}
              alt={localize(
                defineMessage({
                  id: 'viewer-inboxzero2',
                  message: 'Empty inbox',
                }),
              )}
            />
          )}
          <StyledViewerHeaderDiv>
            <Trans id="empty-viewer-header2">Way to go!</Trans>
          </StyledViewerHeaderDiv>
          <StyledViewerBodyDiv>
            <Trans id="empty-viewer-body2">
              Your inbox is clear <InlineEmoji emoji={emojiMap.raised_hands} size={14} />
              <br />
              Keep up the good work!
            </Trans>
          </StyledViewerBodyDiv>
        </>
      );
  }
};
